import { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import AppBar from "@mui/material/AppBar";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import { MarkdownDoc } from "../components/MarkdownDoc";
import { SUPPORT_EMAIL } from "../domain/contact";
import { ENTITY, entityAddressLine, entityIdentifiersLine } from "../domain/entity";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { BrandLockup } from "../components/Brand";
import { ColorModeToggle } from "../components/ColorModeToggle";
import { VersionLine } from "../components/VersionLine";

export const LEGAL_DOCS = {
  terms:         { slug: "terms",         title: "Terms of Service" },
  privacy:       { slug: "privacy",       title: "Privacy Policy" },
  sla:           { slug: "sla",           title: "Service Level Agreement" },
  refunds:       { slug: "refunds",       title: "Cancellation & Refunds" },
  payment:       { slug: "payment",       title: "Payment Terms" },
  aup:           { slug: "aup",           title: "Acceptable Use Policy" },
  dpa:           { slug: "dpa",           title: "Data Processing Addendum" },
  support:       { slug: "support",       title: "Support Policy" },
  security:      { slug: "security",      title: "Security Statement" },
  subprocessors: { slug: "subprocessors", title: "Sub-processors" },
} as const;
export type LegalSlug = keyof typeof LEGAL_DOCS;

type Manifest = { approved: Record<string, boolean> };

/**
 * Renders the PUBLIC edition of a legal document from public/legal/, which is
 * generated by scripts/sync-legal.mjs out of the io.ankhos catalog.
 *
 * 🔑 The draft banner is DATA, not a hardcoded string. It is driven by the
 * `approved:` frontmatter of the source document, carried through into
 * manifest.json by the sync script. When the lawyer signs off, the flag flips
 * in io.ankhos, the sync regenerates, and the banner disappears on its own --
 * nobody has to remember to delete it here. A banner someone must remember to
 * remove is a banner that outlives its truth.
 */
export function LegalDoc() {
  const auth = useAuth();
  const { slug } = useParams<{ slug: string }>();
  const doc = slug && slug in LEGAL_DOCS ? LEGAL_DOCS[slug as LegalSlug] : undefined;

  const [md, setMd] = useState<string | null>(null);
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!doc) return;
    let live = true;
    setMd(null);
    setError(null);
    Promise.all([
      fetch(`/legal/${doc.slug}.md`).then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.text();
      }),
      fetch("/legal/manifest.json").then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([text, mf]) => {
        if (!live) return;
        setMd(text);
        setManifest(mf);
      })
      .catch((e: unknown) => live && setError(String(e)));
    return () => {
      live = false;
    };
  }, [doc]);

  if (!doc) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="warning">
          No such document. <Link component={RouterLink} to="/legal">See all policies</Link>.
        </Alert>
      </Container>
    );
  }

  const approved = manifest?.approved?.[doc.slug];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <BrandLockup to="/welcome" />
          <ColorModeToggle />
          <Button size="small" onClick={() => void auth.signinRedirect()}>
            Sign in
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/legal" variant="body2">
            ← All policies
          </Link>
        </Stack>

        {approved === false && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Draft — not yet in force</AlertTitle>
            This document is published for review. It has not completed legal review, so it does
            not yet bind you or us, and it may change before it does. It is shown rather than
            hidden so you can see the terms we intend to offer.
          </Alert>
        )}

        {error && <Alert severity="error">Could not load this document ({error}).</Alert>}
        {!md && !error && <CircularProgress size={24} />}

        {md && (
          <MarkdownDoc md={md} />
        )}

        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" color="text.secondary">
          {ENTITY.name} ({ENTITY.brand}) is a {ENTITY.form}, {entityIdentifiersLine()}. Registered office:{" "}
          {entityAddressLine().replace(`${ENTITY.name}, `, "")},
          India. These documents are governed
          by the laws of India. Questions about them:{" "}
          <Link component={RouterLink} to="/contact" color="inherit">contact us</Link> or{" "}
          <Link href={`mailto:${SUPPORT_EMAIL}`} color="inherit">{SUPPORT_EMAIL}</Link>.
        </Typography>
      <VersionLine />
      </Container>
    </Box>
  );
}
